import * as React from 'react';
import * as intl from 'react-intl-universal';
import { languages } from 'common/i18n';
import { post } from 'pc-sales/utils/http';

declare global {
    interface Window {
        TencentCaptcha: any;
        grecaptcha: any;
        captchaSimple: Function;
        TCapMsg: Function;
        callbackName: Function;
        captchaAppId: string;
        gSitekey: string;
    }
}

interface iProps {
    value?: string;
    checkApi?: string;
    lazyValidate?: boolean;
    onChange?: (value: string) => any;
    onCall?: (value?: string, type?: string) => any;
    extension?: (captcha: any) => any;
    lang?: string;
    isDestroy?: boolean;
    name?: string;
}

interface iState {
    status: 'success' | 'init' | 'failed' | 'validating' | 'loading';
    useChrome: boolean;
    useTencent: boolean;
    reVerify: boolean;
}

export default class Captcha extends React.Component<iProps, iState> {
    captchaDivRef: React.RefObject<HTMLDivElement> = React.createRef();
    private widget_id: any;

    static tcLangs: any = {
        'zh-CN': 2052,
        'zh-TW': 1028,
        'en-US': 1033,
        'ko-KR': 1033,
        'ja-JP': 1033,
        'vi-VN': 1033,
        'in-ID': 1033,
        'th-TH': 1033,
    };
    static ggLang: { [key: string]: string } = {
        'zh-CN': 'zh-CN',
        'zh-TW': 'zh-TW',
        'en-US': 'en',
        'ko-KR': 'ko',
        'ja-JP': 'ja',
        'vi-VN': 'vi',
        'in-ID': 'id',
        'th-TH': 'th',
    };

    private locales: any = {
        init: intl.get('Hint.ClickToCheck'),
        success: intl.get('Hint.CaptchaSuccess'),
        failed: intl.get('Hint.CaptchaFailed')
    };

    static defaultProps = {
        checkApi: '/v2/common/captcha/tCheck',
        lazyValidate: false
    };

    constructor(props: iProps) {
        super(props);
        const { extension } = this.props;
        this.state = {
            status: 'init',
            useChrome: !!window.grecaptcha && !window.TencentCaptcha,  // 谷歌验证
            useTencent: !!window.TencentCaptcha && !window.grecaptcha,  // 腾讯验证
            reVerify: false
        };
        extension && extension(this.errorHandler);
    }

    /**
     * token校验
     * @param token
     */
    validate = async (res: any) => {
        const { useChrome } = this.state;
        const { onChange } = this.props;
        onChange && onChange('validating');
        this.setState({ status: 'validating' });
        if (useChrome) {
            await post('/v2/common/captcha/gCheck', { token: res });
        } else {
            await post('/v2/common/captcha/tCheck', res);
        }
    };

    /**
     * 成功处理
     */
    successHandler = (res: any) => {
        const { useChrome } = this.state;
        const { onChange, onCall } = this.props;
        onChange && onChange(res);
        onCall && onCall(res, useChrome ? 'gCaptcha' : 'tCaptcha');
        this.setState({ status: 'success', reVerify: false });
    };

    /**
     * 错误处理
     */
    errorHandler = () => {
        const { useChrome, useTencent } = this.state;
        const { onCall } = this.props;
        if (useChrome) {
            this.widget_id && window.grecaptcha.reset(this.widget_id);
            this.setState({ status: 'failed' });
        }
        if (useTencent) {
            this.setState({ status: 'failed' });
        }
        onCall && onCall();
    };

    /**
     * 回调控制
     * @param format
     */
    callbackHandler = (format?: Function) => async (res: any) => {
        const response = typeof format === 'function' ? format(res) : res;
        if (!!response) {
            try {
                const { onChange, lazyValidate } = this.props;
                lazyValidate ? (onChange && onChange(response)) : await this.validate(response);
                this.successHandler(response);
            } catch (err) {
                // 票据验证失败
                this.errorHandler();
            }
        } else {
            // 票据验证失败
            this.errorHandler();
        }
    };

    /**
     * 获取腾讯验证
     */
    isLoadTencent: boolean = false;
    getTencent = () => {
        this.setState({ status: 'loading' });
        if (this.isLoadTencent) return this.setState({ status: 'init' });
        try {
            new window.TencentCaptcha(this.captchaDivRef.current,
                window.captchaAppId,
                async (res: any) => {
                    if (res.ret === 0) {
                        await this.callbackHandler()({
                            ticket: res.ticket,
                            randstr: res.randstr,
                        });
                    } else {
                        this.setState({ status: 'init' });
                    }
                },
                {
                    needFeedBack: false,
                }
            );
            this.isLoadTencent = true;
            this.setState({ status: 'init' });
        } catch (error) {
            console.log({error});
            this.setState({ status: 'init' });
        }
    };

    /**
     * 获取谷歌验证iframe
     */
    getChrome = () => {
        try {
            this.setState({ status: 'init' });
            this.widget_id = window.grecaptcha.render(`${this.props.name}TCaptcha`, {
                'sitekey': window.gSitekey,
                'callback': this.callbackHandler(),
                'expired-callback': () => this.setState({ reVerify: true }),
                'hl': Captcha.ggLang[languages.getDefault()]
            });
        } catch (error) {
            console.log(error,'Google验证重复渲染');
        }
    };

    /**
     * 获取style
     */
    getStyle = (): any => {
        const { useChrome, useTencent, status } = this.state;
        const noService = !useChrome && !useTencent;
        return noService && {
            ...(['init', 'failed'].includes(status) || useChrome  ? {} : { pointerEvents: 'none' }),
            height: '40px',
            border: '1px solid rgb(231, 231, 231)',
            borderRadius: '2px',
            background: 'linear-gradient(0deg,hsla(0,0%,93%,.6),hsla(0,0%,100%,0))',
            lineHeight: '40px',
            textAlign: 'center'
        } || {
            ...(['init', 'failed'].includes(status) || useChrome ? {} : { pointerEvents: 'none' }),
        };
    };

    /**
     * 是否为移动端设备
     */
    isMobile = () => {
        const types = /(iphone|ipod|ipad|android|symbian|mobile|windows phone|blackberry|windows ce)/;
        return navigator.userAgent.toLowerCase().match(types);
    };

    /**
     * 获取类名
     */
    getClassName = () => {
        const { useChrome, useTencent, status, reVerify } = this.state;
        const wrapper = ['TCaptcha-wrapper', `${status}`];
        const body = [`${status}`];
        const float = ['TCaptcha-float'];

        if (useChrome) {
            wrapper.push('google');
            body.push('google');
        }
        if (useTencent) {
            wrapper.push('tencent');
            body.push('tencent TCaptcha-id');
        }
        if (this.isMobile()) {
            // wrapper.push('mobile');
            body.push('mobile');
        }
        if (reVerify) {
            float.push('reVerify');
            wrapper.push('reVerify');
        }
        return {
            wrapper: wrapper.join(' '),
            body: body.join(' '),
            float: float.join(' ')
        };
    };

    // 重新初始化
    reload = () => {
        const useChrome = !!window.grecaptcha && !window.TencentCaptcha;
        const useTencent = !!window.TencentCaptcha && !window.grecaptcha;
        useTencent && this.getTencent();
        useChrome && this.getChrome();
        this.setState({
            useChrome,  // 谷歌验证
            useTencent  // 腾讯验证
        }, () => {
            this.errorHandler();
        });
    };

    componentDidMount() {
        const { useChrome, useTencent } = this.state;
        useTencent && this.getTencent();
        useChrome && this.getChrome();
    }

    componentWillUnmount() {
        const { useTencent } = this.state;
        const { name } = this.props;
        if (!(name === 'unDes')) {
            useTencent && this.getTencent();
        }
    }

    UNSAFE_componentWillReceiveProps({ isDestroy }: any) {
        const { useTencent } = this.state;
        if (isDestroy && isDestroy !== this.props.isDestroy) {
            useTencent && this.getTencent();
        }
        if (!isDestroy && isDestroy !== this.props.isDestroy) {
            useTencent && this.getTencent();
        }
    }

    renderTencentCaptcha = () => {
        const { status } = this.state;
        const TCClassName: any = {
            init: 'tcaptcha-trigger--normal',
            validating: 'tcaptcha-trigger--validating',
            success: 'tcaptcha-trigger--success',
            failed: 'tcaptcha-trigger--normal',
            loading: 'tcaptcha-trigger--initing',
        };
        const TCLocales: any = {
            init: intl.get('Hint.TCClickToCheck'),
            validating: intl.get('Hint.TCCaptchaWaiting'),
            success: intl.get('Hint.CaptchaSuccess'),
            failed: intl.get('Hint.TCCaptchaFailed'),
            loading: intl.get('Component.UploadLoading'),
        };
        return (
            <div className={`form-item-content tcaptcha-trigger clearfix ${TCClassName[status]}`} id="tcaptcha_trigger"
                style={{ height: 38 }} onClick={() => {
                    if (status === 'init' || status === 'failed') {
                        this.setState({ status: 'validating' });
                    }
                }}>
                {
                    status === 'loading'
                        ? <div className="tcaptcha-initing-dots">
                            <div className="tcaptcha-initing-dot dot0"></div>
                            <div className="tcaptcha-initing-dot dot1"></div>
                            <div className="tcaptcha-initing-dot dot2"></div>
                            <div className="tcaptcha-initing-dot dot3"></div>
                        </div>
                        : <div className="tcaptcha-trigger-icon">
                            <div className="tcaptcha-trigger-icon-outer">
                                <div className="tcaptcha-trigger-icon-outer-cover">
                                    <div className="t_iconfont" id="outer_font"></div>
                                </div>
                                <svg viewBox="0 0 32 32">
                                    <circle cx="16" cy="16" r="16"></circle>
                                </svg>
                            </div>
                            {status !== 'success' && <div className="tcaptcha-trigger-icon-inner">
                                <div className="tcaptcha-trigger-icon-inner-cover t_iconfont" id="inner_font"></div>
                            </div>}
                        </div>
                }
                <p className="tcaptcha-trigger-text" id="tcaptcha_trigger_text_init">{TCLocales[status]}</p>
            </div>
        );
    }

    render() {
        const { useChrome, useTencent, status } = this.state;
        const { wrapper, body, float } = this.getClassName();
        const noService = !useChrome && !useTencent;
        return (
            <div className={wrapper}>
                <div id={`${this.props.name}TCaptcha`} ref={this.captchaDivRef} className={body} style={this.getStyle()}>
                    {useTencent && this.renderTencentCaptcha()}
                    {noService
                        ? <span style={{ width: '100%', height: '100%', display: 'inline-block', cursor: 'pointer' }}
                            onClick={this.reload}>{intl.get('Hint.CaptchaInitError')}</span>
                        : ''
                    }
                </div>
                {(useChrome || this.isMobile()) &&
                <div className={float}>{noService || status === 'failed' ?
                    <span style={{
                        width: '100%',
                        height: '100%',
                        display: 'inline-block',
                        cursor: 'pointer',
                        marginLeft: '24px'
                    }} onClick={this.reload}>{intl.get('Hint.CaptchaInitError')}</span>
                    : this.locales[status]}</div>}
            </div>
        );
    }
}
